/**
 * EligibilityChecklist — Dynamic checklist with conditional items.
 * Use case: Civic Benefits — Complex eligibility (e.g., SNAP, housing assistance).
 */
import React from 'react';
import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';

const DEFAULT_ITEMS = [
  { id: 'resident', label: 'Resident of this jurisdiction', required: true },
  { id: 'income', label: 'Household income below threshold', required: true },
  { id: 'assets', label: 'Assets below program limit', required: false },
  { id: 'employed', label: 'Employed or actively seeking work (if required)', required: false },
  { id: 'citizen', label: 'Citizenship or qualified immigrant status', required: true },
];

export default function EligibilityChecklist({
  items = DEFAULT_ITEMS,
  value = {},
  onChange,
  label = 'Eligibility checklist',
  showStatus = true,
  ...props
}) {
  const checked = typeof value === 'object' ? value : {};
  const requiredIds = items.filter((i) => i.required).map((i) => i.id);
  const checkedRequired = requiredIds.filter((id) => checked[id]);
  const allRequiredMet = requiredIds.length === 0 || checkedRequired.length === requiredIds.length;

  const handleToggle = (id) => {
    const next = { ...checked, [id]: !checked[id] };
    onChange?.({ target: { value: next } });
  };

  return (
    <Box sx={{ py: 1 }} {...props}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <FormGroup>
        {items.map((item) => (
          <FormControlLabel
            key={item.id}
            control={
              <Checkbox
                checked={!!checked[item.id]}
                onChange={() => handleToggle(item.id)}
              />
            }
            label={
              <Typography variant="body2">
                {item.label}
                {item.required && (
                  <Typography component="span" variant="caption" color="error.main" sx={{ ml: 0.5 }}>
                    *
                  </Typography>
                )}
              </Typography>
            }
          />
        ))}
      </FormGroup>
      {showStatus && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: allRequiredMet ? 'success.light' : 'warning.light', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {allRequiredMet ? '✓ All required criteria met' : `Complete ${requiredIds.length - checkedRequired.length} more required criteria`}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
